import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  LogOut, User, Shield, Loader2, CheckCircle, Eye, EyeOff,
  Phone, BadgeCheck, Pencil, X, SmartphoneNfc,
} from 'lucide-react';
import { api } from '../lib/api';
import { clearAuth, getCurrentCustomer, getRefreshToken } from '../lib/auth';
import { useBranding } from '../contexts/BrandingContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustomerProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  avatarUrl: string | null;
  country: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  phoneVerifiedAt: string | null;
  status: string;
  createdAt: string;
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const profileSchema = z.object({
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  dateOfBirth: z.string().optional(),
  avatarUrl: z.string().url('Must be a valid URL').or(z.literal('')).optional(),
  country: z.string().max(2).optional(),
  city: z.string().max(100).optional(),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'New password must be at least 8 characters').max(128),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, icon, children }: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-2.5">
        <span className="text-zinc-400">{icon}</span>
        <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Avatar({ profile, size = 'lg' }: { profile: CustomerProfile | null; size?: 'sm' | 'lg' }) {
  const branding = useBranding();
  const dim = size === 'lg' ? 'w-16 h-16 text-2xl' : 'w-10 h-10 text-base';

  if (profile?.avatarUrl) {
    return (
      <img
        src={profile.avatarUrl}
        alt="Profile"
        className={`${dim} rounded-full object-cover border border-zinc-200`}
      />
    );
  }

  const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.email || 'C';
  const initials = name
    .split(' ')
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center font-bold text-white`}
      style={{ backgroundColor: branding.primaryColor }}
    >
      {initials}
    </div>
  );
}

// ─── Profile form ─────────────────────────────────────────────────────────────

function ProfileForm({ profile, onSaved }: { profile: CustomerProfile; onSaved: (p: CustomerProfile) => void }) {
  const [saved, setSaved] = useState(false);
  const [apiError, setApiError] = useState('');

  const { register, handleSubmit, reset, formState: { errors, isSubmitting, isDirty } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      dateOfBirth: profile.dateOfBirth ?? '',
      avatarUrl: profile.avatarUrl ?? '',
      country: profile.country ?? '',
      city: profile.city ?? '',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    setApiError('');
    setSaved(false);
    try {
      const res = await api.patch<CustomerProfile>('/profile', data);
      onSaved(res.data);
      // Reset form with the returned values so isDirty=false and fields show saved data
      reset({
        firstName: res.data.firstName ?? '',
        lastName: res.data.lastName ?? '',
        dateOfBirth: res.data.dateOfBirth ?? '',
        avatarUrl: res.data.avatarUrl ?? '',
        country: res.data.country ?? '',
        city: res.data.city ?? '',
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setApiError('Failed to save changes. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First name" error={errors.firstName?.message}>
          <input type="text" className="input-field" placeholder="Jane" {...register('firstName')} />
        </Field>
        <Field label="Last name" error={errors.lastName?.message}>
          <input type="text" className="input-field" placeholder="Smith" {...register('lastName')} />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Date of birth" error={errors.dateOfBirth?.message}>
          <input type="date" className="input-field" {...register('dateOfBirth')} />
        </Field>
        <Field label="Country (2-letter code)" error={errors.country?.message}>
          <input type="text" className="input-field" placeholder="IT" maxLength={2} {...register('country')} />
        </Field>
      </div>

      <Field label="City" error={errors.city?.message}>
        <input type="text" className="input-field" placeholder="Rome" {...register('city')} />
      </Field>

      <Field label="Avatar URL" error={errors.avatarUrl?.message}>
        <input
          type="url"
          className="input-field"
          placeholder="https://example.com/avatar.jpg"
          {...register('avatarUrl')}
        />
      </Field>

      {apiError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-600">{apiError}</p>
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={isSubmitting || !isDirty}
          className="btn-primary px-5 py-2 text-sm"
        >
          {isSubmitting ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save changes'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle size={14} /> Saved
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Phone section ────────────────────────────────────────────────────────────
// Handles all phone-related states in one place:
//   • no phone saved → form to add a number
//   • phone saved, not verified → OTP verify flow
//   • phone verified → verified badge + option to change

type PhoneStep = 'view' | 'edit' | 'otp';

function PhoneSection({
  profile,
  onPhoneUpdated,
}: {
  profile: CustomerProfile;
  onPhoneUpdated: (p: CustomerProfile) => void;
}) {
  const [step, setStep] = useState<PhoneStep>('view');
  const [newPhone, setNewPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentPhone = profile.phone;
  const isVerified = !!profile.phoneVerifiedAt;

  // ── Save a new/changed phone number ──────────────────────────────────────
  const handleSavePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newPhone.trim();
    if (!trimmed || trimmed.length < 7) { setError('Enter a valid phone number.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.patch<CustomerProfile>('/profile', { phone: trimmed });
      onPhoneUpdated(res.data);
      setStep('view');
      setNewPhone('');
    } catch {
      setError('Failed to save phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Send OTP ──────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/phone/send-otp', { phone: currentPhone });
      setStep('otp');
      setOtp('');
    } catch {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ────────────────────────────────────────────────────────────
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/phone/verify-otp', { phone: currentPhone, code: otp.trim() });
      // Re-fetch profile to get updated phoneVerifiedAt
      const res = await api.get<CustomerProfile>('/profile');
      onPhoneUpdated(res.data);
      setStep('view');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 400 ? 'Invalid or expired code.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── View: no phone yet ────────────────────────────────────────────────────
  if (!currentPhone && step === 'view') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-zinc-500">
          Add a phone number to enable SMS login and receive loyalty updates.
        </p>
        <button
          type="button"
          onClick={() => { setStep('edit'); setNewPhone(''); setError(''); }}
          className="inline-flex items-center gap-2 text-sm font-medium text-[--color-primary] hover:opacity-80 transition-opacity"
        >
          <Phone size={15} /> Add phone number
        </button>
      </div>
    );
  }

  // ── Edit: add / change phone number ──────────────────────────────────────
  if (step === 'edit') {
    return (
      <form onSubmit={handleSavePhone} className="space-y-3">
        <div>
          <label htmlFor="new-phone" className="block text-sm font-medium text-zinc-700 mb-1.5">
            {currentPhone ? 'New phone number' : 'Phone number'}
          </label>
          <div className="flex gap-2">
            <input
              id="new-phone"
              type="tel"
              autoComplete="tel"
              className="input-field flex-1"
              placeholder="+39 333 000 0000"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              autoFocus
            />
            <button type="submit" disabled={loading} className="btn-primary px-4 py-2 text-sm">
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('view'); setError(''); }}
              className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors"
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>
          <p className="mt-1.5 text-xs text-zinc-400">Include country code, e.g. +39 for Italy</p>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </form>
    );
  }

  // ── OTP step: enter the code ──────────────────────────────────────────────
  if (step === 'otp') {
    return (
      <form onSubmit={handleVerifyOtp} className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
          <SmartphoneNfc size={18} className="text-blue-500 shrink-0" />
          <p className="text-sm text-blue-700">
            A 6-digit code has been sent to <strong>{currentPhone}</strong>. Enter it below to verify.
          </p>
        </div>
        <div>
          <label htmlFor="verify-otp" className="block text-sm font-medium text-zinc-700 mb-1.5">
            Verification code
          </label>
          <div className="flex gap-2">
            <input
              id="verify-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="input-field tracking-widest text-center font-mono w-36"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="btn-primary px-4 py-2 text-sm"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('view'); setOtp(''); setError(''); }}
              className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors"
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="button"
          onClick={handleSendOtp}
          disabled={loading}
          className="text-xs text-zinc-400 hover:text-zinc-600 underline underline-offset-2 disabled:opacity-50"
        >
          Resend code
        </button>
      </form>
    );
  }

  // ── View: phone present ───────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-medium text-zinc-800">{currentPhone}</span>
          {isVerified ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <BadgeCheck size={11} /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              <Phone size={11} /> Not verified
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setStep('edit'); setNewPhone(currentPhone ?? ''); setError(''); }}
          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
        >
          <Pencil size={12} /> Change
        </button>
      </div>

      {!isVerified && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
          <Phone size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-800 font-medium">Verify your phone number</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Verify to enable SMS login and receive important account notifications.
            </p>
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={loading}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 hover:text-amber-700 disabled:opacity-50 transition-colors"
            >
              {loading ? <><Loader2 size={13} className="animate-spin" /> Sending…</> : 'Send verification code →'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ─── Change password form ─────────────────────────────────────────────────────

function ChangePasswordForm() {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saved, setSaved] = useState(false);
  const [apiError, setApiError] = useState('');

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data: PasswordFormData) => {
    setApiError('');
    setSaved(false);
    try {
      await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setSaved(true);
      reset();
      setTimeout(() => setSaved(false), 4000);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        setApiError('Current password is incorrect.');
      } else {
        setApiError('Failed to update password. Please try again.');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Field label="Current password" error={errors.currentPassword?.message}>
        <PasswordInput
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          register={register('currentPassword')}
          placeholder="Your current password"
          autoComplete="current-password"
        />
      </Field>
      <Field label="New password" error={errors.newPassword?.message}>
        <PasswordInput
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          register={register('newPassword')}
          placeholder="Minimum 8 characters"
          autoComplete="new-password"
        />
      </Field>
      <Field label="Confirm new password" error={errors.confirmPassword?.message}>
        <PasswordInput
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          register={register('confirmPassword')}
          placeholder="Repeat new password"
          autoComplete="new-password"
        />
      </Field>

      {apiError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-600">{apiError}</p>
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <button type="submit" disabled={isSubmitting} className="btn-primary px-5 py-2 text-sm">
          {isSubmitting ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : 'Update password'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle size={14} /> Password updated
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Shared field helpers ─────────────────────────────────────────────────────

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-700 mb-1.5">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function PasswordInput({
  show, onToggle, register, placeholder, autoComplete,
}: {
  show: boolean;
  onToggle: () => void;
  register: object;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        className="input-field pr-10"
        placeholder={placeholder}
        {...register}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600"
        tabIndex={-1}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ProfilePage() {
  const navigate = useNavigate();
  const branding = useBranding();
  const currentUser = getCurrentCustomer();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<CustomerProfile>('/profile')
      .then((res) => setProfile(res.data))
      .catch(() => { /* profile stays null, graceful degradation */ })
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    try {
      const rt = getRefreshToken();
      if (rt) await api.post('/customer/auth/logout', { refreshToken: rt });
    } catch { /* ignore API errors on logout */ }
    clearAuth();
    navigate('/login', { replace: true });
  };

  const displayName = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email || 'Customer'
    : currentUser
    ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || currentUser.email || 'Customer'
    : 'Customer';

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top nav */}
      <header className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.name} className="h-7 w-auto object-contain" />
            ) : (
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {branding.name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-sm font-semibold text-zinc-900">{branding.name}</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Hero identity */}
        <div className="flex items-center gap-4">
          <Avatar profile={profile} size="lg" />
          <div>
            <h1 className="text-xl font-bold text-zinc-900">{displayName}</h1>
            {profile?.email && (
              <p className="text-sm text-zinc-500 mt-0.5">{profile.email}</p>
            )}
          </div>
        </div>

        {/* Profile section */}
        <SectionCard title="Profile" icon={<User size={16} />}>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400 py-4">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : profile ? (
            <ProfileForm profile={profile} onSaved={setProfile} />
          ) : (
            <p className="text-sm text-zinc-400">Could not load profile information.</p>
          )}
        </SectionCard>

        {/* Phone section */}
        <SectionCard title="Phone number" icon={<Phone size={16} />}>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400 py-4">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : profile ? (
            <PhoneSection profile={profile} onPhoneUpdated={setProfile} />
          ) : (
            <p className="text-sm text-zinc-400">Could not load profile information.</p>
          )}
        </SectionCard>

        {/* Security section */}
        <SectionCard title="Security" icon={<Shield size={16} />}>
          <ChangePasswordForm />
        </SectionCard>
      </main>
    </div>
  );
}
